import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Button from '../../components/ui/Button';
import CustomerFilters from './components/CustomerFilters';
import CustomerTable from './components/CustomerTable';
import CustomerMobileCard from './components/CustomerMobileCard';
import AddCustomerModal from './components/AddCustomerModal';
import CustomerProfileModal from './components/CustomerProfileModal';
import CustomerPaymentsModal from './components/CustomerPaymentsModal';
import CustomerStatementModal from './components/CustomerStatementModal';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from '../../hooks/useCustomers';
import { getCurrentCompanyId } from '../../utils/auth';

const pad = (n) => String(n).padStart(2, '0');
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const getMonthStart = () => { const n = new Date(); return localDate(new Date(n.getFullYear(), n.getMonth(), 1)); };
const getMonthEnd   = () => { const n = new Date(); return localDate(new Date(n.getFullYear(), n.getMonth() + 1, 0)); };

const CustomerManagement = () => {
  const defaultStart = getMonthStart();
  const defaultEnd   = getMonthEnd();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [sortConfig, setSortConfig] = useState({ column: 'customerName', direction: 'asc' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Get company ID and use React Query hooks
  const companyId = getCurrentCompanyId();
  const { data: customers = [], isLoading: loading, error, refetch } = useCustomers(companyId, startDate, endDate);
  const createCustomerMutation = useCreateCustomer();
  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();

  console.log('Customer Management - Company ID:', companyId);
  console.log('Customer Management - Customers data:', customers);

  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customers?.filter(customer => {
      const matchesSearch = 
        customer?.customerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        customer?.contactPerson?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        customer?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || customer?.category?.toLowerCase() === categoryFilter?.toLowerCase();

      return matchesSearch && matchesCategory;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'endingBalance' || sortConfig?.column === 'creditLimit') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [customers, searchTerm, categoryFilter, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  };

  const handleViewProfile = (customer) => {
    setSelectedCustomer(customer);
    setIsProfileModalOpen(true);
  };

  const handleViewPayments = (customer) => {
    setSelectedCustomer(customer);
    setIsPaymentsModalOpen(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setIsAddModalOpen(true);
  };

  const handleDelete = async (customer) => {
    if (window.confirm(`Are you sure you want to delete ${customer?.customerName}?`)) {
      try {
        await deleteCustomerMutation.mutateAsync(customer.id);
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('Failed to delete customer. Please try again.');
      }
    }
  };

  const handleSendStatement = (customer) => {
    setSelectedCustomer(customer);
    setIsStatementModalOpen(true);
  };

  const handleCreateCustomer = async (customerData, customerId = null) => {
    try {
      if (customerId) {
        // Update existing customer
        await updateCustomerMutation.mutateAsync({ 
          id: customerId, 
          data: { ...customerData, companyId } 
        });
        setEditingCustomer(null);
      } else {
        // Create new customer
        await createCustomerMutation.mutateAsync({ 
          ...customerData, 
          companyId 
        });
      }
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Failed to save customer. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar actions={[
        {
          label: 'View All Payments',
          icon: 'CreditCard',
          onClick: () => {
            // You could implement a view all payments functionality here
            alert('View all customer payments functionality can be implemented here');
          }
        }
      ]} />
      <main className="pt-16">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                Customer Management
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Manage customer profiles, credit terms, and transaction history
              </p>
            </div>

            <Button
              variant="default"
              size="lg"
              onClick={() => setIsAddModalOpen(true)}
              iconName="Plus"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              Add Customer
            </Button>
          </div>

          <CustomerFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onReset={handleResetFilters}
            totalResults={filteredAndSortedCustomers?.length}
          />

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="mt-4 text-muted-foreground">Loading customers...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-error mb-4">Failed to load customers</p>
              <Button onClick={() => refetch()} variant="outline">
                Try Again
              </Button>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No customers found</p>
              <Button onClick={() => setIsAddModalOpen(true)} variant="default">
                Add Your First Customer
              </Button>
            </div>
          ) : (
            <>
              <div className="hidden md:block">
                <CustomerTable
                  customers={filteredAndSortedCustomers}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onViewProfile={handleViewProfile}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSendStatement={handleSendStatement}
                  onViewPayments={handleViewPayments}
                />
              </div>

              <div className="md:hidden space-y-4">
                {filteredAndSortedCustomers?.map(customer => (
                  <CustomerMobileCard
                    key={customer?.id}
                    customer={customer}
                    onViewProfile={handleViewProfile}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSendStatement={handleSendStatement}
                    onViewPayments={handleViewPayments}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleCreateCustomer}
        customer={editingCustomer}
      />

      <CustomerProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        customer={selectedCustomer}
      />

      <CustomerPaymentsModal
        isOpen={isPaymentsModalOpen}
        onClose={() => setIsPaymentsModalOpen(false)}
        customer={selectedCustomer}
        startDate={startDate}
        endDate={endDate}
      />

      <CustomerStatementModal
        isOpen={isStatementModalOpen}
        onClose={() => setIsStatementModalOpen(false)}
        customer={selectedCustomer}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />
    </div>
  );
};

export default CustomerManagement;